import React, { useEffect, useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { usePeriod } from "@/contexts/PeriodContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "@/components/ui/table";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import DownloadLinkDialog from "@/components/DownloadLinkDialog";
import BilanzView from "@/components/BilanzView";
import VeraenderungView from "@/components/VeraenderungView";
import ManualSnapshotDialog from "@/components/ManualSnapshotDialog";
import { Camera, Lock, LockOpen, Archive, ShieldAlert, FileSpreadsheet, ShieldCheck, FileText, LayoutGrid, Table as TableIcon, TrendingUp, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { eur, num, stichtag } from "@/lib/format";

const BUCKETS = [0, 25, 50, 75, 100];

export default function Snapshots() {
    const { activeTenant } = useTenant();
    const { zeitraum, setZeitraum } = usePeriod();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";

    const [snapshots, setSnapshots] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [closeOpen, setCloseOpen] = useState(false);
    const [closeConfirm, setCloseConfirm] = useState("");
    const [closing, setClosing] = useState(false);
    const [unlockOpen, setUnlockOpen] = useState(false);
    const [unlockConfirm, setUnlockConfirm] = useState("");
    const [unlocking, setUnlocking] = useState(false);
    const [dlDialog, setDlDialog] = useState({ open: false, url: "", filename: "", expiresInSec: 600 });
    const [viewMode, setViewMode] = useState("kompakt"); // "kompakt" | "bilanz" | "veraenderung"
    const [manualOpen, setManualOpen] = useState(false);
    const [veraenderung, setVeraenderung] = useState(null);
    const [veraendLoading, setVeraendLoading] = useState(false);

    const load = async () => {
        if (!activeTenant) return;
        setLoading(true);
        try {
            const r = await api.get("/snapshots", { params: { tenant_id: activeTenant.tenant_id } });
            setSnapshots(r.data);
            // Aktuell gewählten Zeitraum bevorzugen
            const match = r.data.find((s) => s.zeitraum === zeitraum) || r.data[0];
            setSelected(match || null);
        } catch {
            toast.error("Snapshots konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTenant?.tenant_id]);

    // Wenn der globale Zeitraum geändert wird (z.B. via Dropdown), den passenden
    // Snapshot der bereits geladenen Liste automatisch auswählen.
    useEffect(() => {
        if (!snapshots.length) return;
        const match = snapshots.find((s) => s.zeitraum === zeitraum);
        if (match) setSelected(match);
    }, [zeitraum, snapshots]);

    // Veränderungs-Daten laden, sobald der "veraenderung"-Tab aktiv ist
    // oder der ausgewählte Snapshot wechselt.
    useEffect(() => {
        if (!selected?.id || !activeTenant?.tenant_id) {
            setVeraenderung(null);
            return;
        }
        if (viewMode !== "veraenderung") return;
        let abort = false;
        setVeraendLoading(true);
        api.get("/snapshots/veraenderung", {
            params: {
                tenant_id: activeTenant.tenant_id,
                zeitraum: selected.zeitraum,
                zeitraum_typ: selected.zeitraum_typ,
            },
        })
            .then((r) => { if (!abort) setVeraenderung(r.data); })
            .catch((e) => {
                if (!abort) {
                    setVeraenderung(null);
                    toast.error(e?.response?.data?.detail || "Veränderung konnte nicht geladen werden");
                }
            })
            .finally(() => { if (!abort) setVeraendLoading(false); });
        return () => { abort = true; };
    }, [viewMode, selected?.id, selected?.zeitraum, selected?.zeitraum_typ, activeTenant?.tenant_id]);

    const createSnapshot = async () => {
        setCreating(true);
        try {
            const r = await api.post("/snapshots", {
                tenant_id: activeTenant.tenant_id,
                zeitraum,
                zeitraum_typ: "jahr",
                notiz: "",
            });
            toast.success(`Snapshot ${zeitraum} gespeichert (${r.data.summen.anzahl_titel} Titel)`);
            await load();
            setSelected(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Snapshot konnte nicht erstellt werden");
        } finally {
            setCreating(false);
        }
    };

    const runAbschluss = async () => {
        setClosing(true);
        try {
            await api.post("/snapshots/abschluss", {
                tenant_id: activeTenant.tenant_id,
                zeitraum: selected.zeitraum,
                zeitraum_typ: selected.zeitraum_typ,
                bestaetigung: closeConfirm,
            });
            toast.success(`Jahresabschluss ${selected.zeitraum} versiegelt`);
            setCloseOpen(false);
            setCloseConfirm("");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Abschluss fehlgeschlagen");
        } finally {
            setClosing(false);
        }
    };

    const runUnlock = async () => {
        setUnlocking(true);
        try {
            await api.post("/snapshots/abschluss/aufheben", {
                tenant_id: activeTenant.tenant_id,
                zeitraum: selected.zeitraum,
                zeitraum_typ: selected.zeitraum_typ,
                bestaetigung: unlockConfirm,
            });
            toast.success(`Abschluss ${selected.zeitraum} aufgehoben`);
            setUnlockOpen(false);
            setUnlockConfirm("");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Aufheben fehlgeschlagen");
        } finally {
            setUnlocking(false);
        }
    };

    const downloadExcel = async () => {
        await _downloadSnapshot("snapshot-xlsx", "export-token.xlsx", "xlsx");
    };

    const downloadPdf = async () => {
        await _downloadSnapshot("snapshot-pdf", "export-token.pdf", "pdf");
    };

    const _downloadSnapshot = async (purpose, pathSuffix, ext) => {
        try {
            // Token-basierter Download: umgeht den Iframe-Sandbox-Block
            // des Emergent-Previews. Der Dialog öffnet einen neuen Tab und
            // zeigt die URL als Fallback zum Kopieren.
            const t = await api.post("/admin/download-token", {
                purpose,
                tenant_id: activeTenant.tenant_id,
                snapshot_id: selected.id,
            });
            const token = t.data?.token;
            const ttl = t.data?.expires_in || 600;
            if (!token) throw new Error("Kein Download-Token erhalten");

            const url = `${process.env.REACT_APP_BACKEND_URL}/api/snapshots/${selected.id}/${pathSuffix}`
                + `?token=${encodeURIComponent(token)}`
                + `&tenant_id=${encodeURIComponent(activeTenant.tenant_id)}`;
            const filename = `snapshot_${activeTenant.tenant_id}_${selected.zeitraum}.${ext}`;
            setDlDialog({ open: true, url, filename, expiresInSec: ttl });
        } catch (e) {
            toast.error(`${ext.toUpperCase()}-Export fehlgeschlagen: ${e?.response?.data?.detail || e.message || "Unbekannter Fehler"}`);
        }
    };

    // Matrix: Zeilen=Erlösgruppen, Spalten=WB-Buckets
    const renderMatrix = () => {
        if (!selected) return null;
        const erloesgruppen = Array.from(
            new Set(selected.aufschluesselung.map((r) => r.erloesgruppe)),
        ).sort();
        const lookup = {};
        for (const r of selected.aufschluesselung) {
            lookup[`${r.erloesgruppe}::${r.wb_bucket}`] = r;
        }
        return (
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Erlösgruppe</TableHead>
                        {BUCKETS.map((b) => (
                            <TableHead key={b} className="text-right">WB {b} %</TableHead>
                        ))}
                        <TableHead className="text-right">Σ Bilanz</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {erloesgruppen.map((eg) => {
                        let total = 0;
                        return (
                            <TableRow key={eg}>
                                <TableCell className="text-sm font-medium" title={eg}>
                                    {eg}
                                </TableCell>
                                {BUCKETS.map((b) => {
                                    const r = lookup[`${eg}::${b}`];
                                    if (r) total += r.bilanzwert;
                                    return (
                                        <TableCell key={b} className="text-right tabular text-xs">
                                            {r ? (
                                                <>
                                                    <div>{eur(r.bilanzwert)}</div>
                                                    <div className="text-[10px] text-muted-foreground">
                                                        {r.anzahl_titel} Titel
                                                    </div>
                                                </>
                                            ) : (
                                                <span className="text-muted-foreground">—</span>
                                            )}
                                        </TableCell>
                                    );
                                })}
                                <TableCell className="text-right tabular font-medium">
                                    {eur(total)}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        );
    };

    return (
        <div className="space-y-6 pb-12 animate-fade-up">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Daten</div>
                    <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-bold mt-1">
                        Snapshots & Jahresabschluss
                    </h1>
                    <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
                        Erstellen Sie eine Momentaufnahme der Bilanzwerte für einen Zeitraum, aggregiert nach
                        Erlösgruppe × Wertberichtigungsstufe (0/25/50/75/100 %). Der Snapshot ist eine
                        <strong className="text-foreground"> reine Berichts­schicht</strong> — die Detail-Bewegungen
                        bleiben dauerhaft erhalten. Der Jahres­abschluss versiegelt den Snapshot
                        unveränderlich für die Buchhaltung und sperrt erneute Importe dieses Jahres.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                        Bewertungszeitraum
                    </Label>
                    <select
                        value={zeitraum}
                        onChange={(e) => setZeitraum(e.target.value)}
                        data-testid="snap-zeitraum-select"
                        className="h-10 px-3 rounded-md border border-border bg-surface1 text-sm"
                    >
                        {["2023", "2024", "2025", "2026"].map((y) => (
                            <option key={y} value={y}>
                                Jahr {y}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Hinweis-Karte: Datensicherheit */}
            <Card className="p-4 bg-emerald-500/10 border-emerald-500/30">
                <div className="flex items-start gap-3">
                    <ShieldCheck className="h-5 w-5 text-emerald-400 mt-0.5" />
                    <div className="text-sm">
                        <div className="font-medium text-emerald-300">Ihre Detaildaten bleiben erhalten</div>
                        <div className="text-muted-foreground mt-0.5">
                            Beim Snapshot- und Jahresabschluss werden <strong>keine</strong> Bewegungsdaten
                            gelöscht. Sie können jederzeit in die Detailansicht jedes Titels und jedes Jahres
                            springen.
                        </div>
                    </div>
                </div>
            </Card>

            {/* Toolbar */}
            <Card className="p-4 bg-surface1 border-border">
                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <Label className="text-xs">Zeitraum</Label>
                        <Input
                            value={zeitraum}
                            onChange={(e) => setZeitraum(e.target.value)}
                            className="mt-1 w-[120px] tabular"
                            data-testid="snap-period-input"
                        />
                    </div>
                    <Button
                        onClick={createSnapshot}
                        disabled={creating || !isAdmin}
                        className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                        data-testid="snap-create-btn"
                    >
                        <Camera className="h-4 w-4 mr-2" />
                        {creating ? "Erstelle …" : `Snapshot ${zeitraum} speichern`}
                    </Button>
                    {isAdmin && (
                        <Button
                            variant="outline"
                            onClick={() => setManualOpen(true)}
                            className="border-border"
                            data-testid="snap-manual-baseline-btn"
                            title="Manuelle Baseline anlegen (z. B. 2023) — für Jahre ohne Bewegungsdaten"
                        >
                            <PencilLine className="h-4 w-4 mr-2" />
                            Baseline manuell anlegen
                        </Button>
                    )}
                    {!isAdmin && (
                        <p className="text-xs text-muted-foreground">
                            Nur Administratoren dürfen Snapshots erstellen.
                        </p>
                    )}
                </div>
            </Card>

            {/* Snapshot-Liste */}
            <Card className="p-4 bg-surface1 border-border">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">
                    Vorhandene Snapshots
                </div>
                {loading && <div className="text-sm text-muted-foreground">Lade …</div>}
                {!loading && snapshots.length === 0 && (
                    <div className="text-sm text-muted-foreground">
                        Noch keine Snapshots für {activeTenant?.name} vorhanden.
                    </div>
                )}
                {!loading && snapshots.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {snapshots.map((s) => (
                            <button
                                key={s.id}
                                onClick={() => setSelected(s)}
                                className={`px-3 py-2 rounded-md text-sm border ${
                                    selected?.id === s.id
                                        ? "bg-tenant text-[color:var(--tenant-accent-foreground)] border-tenant"
                                        : "border-border hover:border-tenant"
                                }`}
                                data-testid={`snap-tab-${s.zeitraum}`}
                            >
                                <div className="font-medium">
                                    {s.zeitraum} {s.abgeschlossen && <Lock className="inline h-3 w-3 ml-1" />}
                                    {s.manual && (
                                        <span className="ml-1 text-[9px] uppercase tracking-wider opacity-80">
                                            manuell
                                        </span>
                                    )}
                                </div>
                                <div className="text-[10px] opacity-80">
                                    {s.summen.anzahl_titel} Titel · {eur(s.summen.bilanzwert)}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </Card>

            {/* Detail */}
            {selected && (
                <Card className="p-5 bg-surface1 border-border">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                                Snapshot {selected.zeitraum_typ}/{selected.zeitraum}
                            </div>
                            <div className="font-heading text-2xl font-semibold mt-1">
                                {eur(selected.summen.bilanzwert)}
                                {selected.abgeschlossen && (
                                    <span className="ml-3 inline-flex items-center gap-1 text-sm text-red-400 font-normal">
                                        <Lock className="h-4 w-4" /> Abgeschlossen
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                                Erstellt am {new Date(selected.erstellt_am).toLocaleString("de-DE")} von{" "}
                                {selected.erstellt_von}
                                {selected.abgeschlossen && selected.abgeschlossen_am && (
                                    <>
                                        {" · "}Abgeschlossen am{" "}
                                        {new Date(selected.abgeschlossen_am).toLocaleString("de-DE")} von{" "}
                                        {selected.abgeschlossen_von}
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {/* View-Toggle: Kompakt vs. Bilanz-Ansicht vs. Veränderung */}
                            <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="snap-view-toggle">
                                <button
                                    type="button"
                                    onClick={() => setViewMode("kompakt")}
                                    className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition ${
                                        viewMode === "kompakt"
                                            ? "bg-tenant text-[color:var(--tenant-accent-foreground)]"
                                            : "bg-transparent text-muted-foreground hover:bg-surface2"
                                    }`}
                                    data-testid="snap-view-kompakt"
                                >
                                    <LayoutGrid className="h-3.5 w-3.5" /> Matrix
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode("bilanz")}
                                    className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition ${
                                        viewMode === "bilanz"
                                            ? "bg-tenant text-[color:var(--tenant-accent-foreground)]"
                                            : "bg-transparent text-muted-foreground hover:bg-surface2"
                                    }`}
                                    data-testid="snap-view-bilanz"
                                >
                                    <TableIcon className="h-3.5 w-3.5" /> Bilanz-Ansicht
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode("veraenderung")}
                                    className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition ${
                                        viewMode === "veraenderung"
                                            ? "bg-tenant text-[color:var(--tenant-accent-foreground)]"
                                            : "bg-transparent text-muted-foreground hover:bg-surface2"
                                    }`}
                                    data-testid="snap-view-veraenderung"
                                >
                                    <TrendingUp className="h-3.5 w-3.5" /> Veränderung zum Vorjahr
                                </button>
                            </div>
                            <Button
                                variant="outline"
                                onClick={downloadPdf}
                                className="border-border"
                                data-testid="snap-pdf-btn"
                                title="Bilanz als PDF herunterladen (Excel-Layout)"
                            >
                                <FileText className="h-4 w-4 mr-2" />
                                PDF-Export
                            </Button>
                            <Button
                                variant="outline"
                                onClick={downloadExcel}
                                className="border-border"
                                data-testid="snap-excel-btn"
                            >
                                <FileSpreadsheet className="h-4 w-4 mr-2" />
                                Excel-Export
                            </Button>
                            {isAdmin && !selected.abgeschlossen && (
                                <Button
                                    variant="outline"
                                    onClick={() => setCloseOpen(true)}
                                    className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                                    data-testid="snap-abschluss-btn"
                                >
                                    <Archive className="h-4 w-4 mr-2" />
                                    Jahresabschluss
                                </Button>
                            )}
                            {isAdmin && selected.abgeschlossen && (
                                <Button
                                    variant="outline"
                                    onClick={() => setUnlockOpen(true)}
                                    className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                                    data-testid="snap-unlock-btn"
                                >
                                    <LockOpen className="h-4 w-4 mr-2" />
                                    Abschluss aufheben
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Druck-Header: nur sichtbar im Print-Output */}
                    <div className="print-only mb-4">
                        <div style={{ fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#666" }}>
                            INVENTUR PRO · Bilanz-Snapshot
                        </div>
                        <div style={{ fontSize: "16px", fontWeight: 700, marginTop: 4 }}>
                            {activeTenant?.name} — Stichtag {stichtag(selected.zeitraum, selected.zeitraum_typ)}
                        </div>
                        <div style={{ fontSize: "11px", color: "#444", marginTop: 2 }}>
                            Erstellt am {new Date(selected.erstellt_am).toLocaleString("de-DE")} von {selected.erstellt_von}
                            {selected.abgeschlossen && (
                                <> · ABGESCHLOSSEN am {new Date(selected.abgeschlossen_am).toLocaleString("de-DE")}</>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-4 gap-3 mb-5 text-sm">
                        <div className="p-3 rounded-md bg-surface2 border border-border">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                Stück auf Lager
                            </div>
                            <div className="tabular font-medium">{num(selected.summen.lager_gesamt || 0)}</div>
                            {selected.summen.anzahl_titel ? (
                                <div className="text-[10px] text-muted-foreground/80 tabular mt-0.5">
                                    Σ {num(selected.summen.anzahl_titel)} Titel
                                </div>
                            ) : null}
                        </div>
                        <div className="p-3 rounded-md bg-surface2 border border-border">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                Lagerwert
                            </div>
                            <div className="tabular font-medium">{eur(selected.summen.lagerwert)}</div>
                        </div>
                        <div className="p-3 rounded-md bg-surface2 border border-border">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                Wertberichtigung
                            </div>
                            <div className="tabular font-medium">{eur(selected.summen.wertberichtigung)}</div>
                        </div>
                        <div className="p-3 rounded-md bg-surface2 border border-border">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                Bilanzwert
                            </div>
                            <div className="tabular font-medium text-emerald-400">
                                {eur(selected.summen.bilanzwert)}
                            </div>
                        </div>
                    </div>

                    {viewMode === "bilanz" && <BilanzView snapshot={selected} />}
                    {viewMode === "kompakt" && <div className="overflow-auto">{renderMatrix()}</div>}
                    {viewMode === "veraenderung" && (
                        veraendLoading ? (
                            <div className="text-sm text-muted-foreground">Lade Veränderung …</div>
                        ) : (
                            <VeraenderungView data={veraenderung} />
                        )
                    )}

                    <div className="mt-3 text-xs text-muted-foreground">
                        Σ {num(selected.summen.anzahl_titel)} Titel · Anzahl Aggregat-Zeilen:{" "}
                        {selected.aufschluesselung.length}
                    </div>
                </Card>
            )}

            {/* Abschluss-Dialog */}
            <AlertDialog open={closeOpen} onOpenChange={setCloseOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <ShieldAlert className="h-5 w-5 text-red-400" />
                            Jahresabschluss für {selected?.zeitraum}?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm">
                                <p>Folgendes passiert:</p>
                                <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                                    <li>
                                        Der Snapshot wird <strong>unveränderlich</strong> versiegelt
                                        (für Buchhaltung / Finanzamt).
                                    </li>
                                    <li>
                                        Ihre <strong>Detail-Bewegungsdaten bleiben vollständig erhalten</strong>{" "}
                                        — sie werden nicht gelöscht.
                                    </li>
                                    <li>
                                        Es ist <strong>kein erneuter Import</strong> dieses Jahres mehr
                                        möglich, solange der Abschluss aktiv ist (kann vom Admin wieder
                                        aufgehoben werden).
                                    </li>
                                </ul>
                                <div>
                                    <Label className="text-xs">Zur Bestätigung „ABSCHLUSS" eintippen</Label>
                                    <Input
                                        value={closeConfirm}
                                        onChange={(e) => setCloseConfirm(e.target.value)}
                                        placeholder="ABSCHLUSS"
                                        className="mt-1"
                                        data-testid="snap-abschluss-confirm-input"
                                    />
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={closing}>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={runAbschluss}
                            disabled={closing || closeConfirm.trim().toUpperCase() !== "ABSCHLUSS"}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="snap-abschluss-confirm"
                        >
                            <Archive className="h-4 w-4 mr-2" />
                            {closing ? "Schließe ab …" : "Ja, endgültig abschließen"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Aufheben-Dialog */}
            <AlertDialog open={unlockOpen} onOpenChange={setUnlockOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <LockOpen className="h-5 w-5 text-amber-400" />
                            Abschluss {selected?.zeitraum} aufheben?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm">
                                <p>
                                    Der Snapshot wird wieder als Entwurf markiert und kann überschrieben
                                    werden. Importe für dieses Jahr werden wieder zugelassen.
                                </p>
                                <div>
                                    <Label className="text-xs">Zur Bestätigung „AUFHEBEN" eintippen</Label>
                                    <Input
                                        value={unlockConfirm}
                                        onChange={(e) => setUnlockConfirm(e.target.value)}
                                        placeholder="AUFHEBEN"
                                        className="mt-1"
                                        data-testid="snap-unlock-confirm-input"
                                    />
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={unlocking}>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={runUnlock}
                            disabled={unlocking || unlockConfirm.trim().toUpperCase() !== "AUFHEBEN"}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="snap-unlock-confirm"
                        >
                            <LockOpen className="h-4 w-4 mr-2" />
                            {unlocking ? "Hebe auf …" : "Ja, Abschluss aufheben"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <DownloadLinkDialog
                open={dlDialog.open}
                onOpenChange={(o) => setDlDialog((d) => ({ ...d, open: o }))}
                url={dlDialog.url}
                title={dlDialog.filename?.endsWith(".pdf") ? "Snapshot als PDF herunterladen" : "Snapshot als Excel herunterladen"}
                description="Der Download wird in einem neuen Tab gestartet. Falls Ihr Browser den neuen Tab blockiert, kopieren Sie die URL und öffnen Sie sie manuell in einem neuen Browser-Tab."
                filename={dlDialog.filename}
                expiresInSec={dlDialog.expiresInSec}
            />

            <ManualSnapshotDialog
                open={manualOpen}
                onOpenChange={setManualOpen}
                initialZeitraum="2023"
                initialZeitraumTyp="jahr"
                erloesgruppenVorgaben={
                    // Erlösgruppen aus dem zuletzt vorhandenen Snapshot übernehmen
                    snapshots[0]
                        ? Array.from(new Set((snapshots[0].aufschluesselung || []).map((r) => r.erloesgruppe))).sort()
                        : []
                }
                onSaved={async (snap) => {
                    await load();
                    setSelected(snap);
                }}
            />
        </div>
    );
}
